import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SignInInputSchema, type SignInInput, type UserPublic } from '@forest/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { api, apiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/sessionStore';
import { useUiStore } from '@/stores/uiStore';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';

const LANGS: { key: 'ro' | 'ru' | 'en'; label: string }[] = [
  { key: 'ro', label: 'RO' },
  { key: 'ru', label: 'RU' },
  { key: 'en', label: 'EN' },
];

export function LoginPage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const pushToast = useUiStore((s) => s.pushToast);
  const qc = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) navigate('/app/database', { replace: true });
  }, [user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInInput>({
    resolver: zodResolver(SignInInputSchema),
    defaultValues: { username: '', password: '' },
  });

  const mut = useMutation({
    mutationFn: (data: SignInInput) =>
      api.post('auth/sign-in/username', { json: data }).json<{ user: UserPublic }>(),
    onSuccess: (data) => {
      setUser(data.user);
      void qc.invalidateQueries({ queryKey: ['auth', 'session'] });
      navigate('/app/database', { replace: true });
    },
    onError: async (e) => {
      const err = await apiError(e);
      // The server's own message is the useful one for a bad password. A
      // lockout or an unreachable backend needs saying in the user's language:
      // those used to surface as "Bad request" and "Internal server error".
      const key =
        err.code === 'RATE_LIMITED'
          ? 'login.rateLimited'
          : err.code === 'INTERNAL' || (err.status !== null && err.status >= 500)
            ? 'login.serverUnavailable'
            : null;
      pushToast({ variant: 'error', message: key ? t(key) : err.message || t('login.error') });
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-900">
      <Card className="w-full max-w-md">
        <CardBody>
          <div className="mb-4 flex justify-end gap-1">
            {LANGS.map((l) => (
              <button
                key={l.key}
                type="button"
                onClick={() => void i18n.changeLanguage(l.key)}
                className={`rounded px-2.5 py-1 text-sm font-medium transition ${
                  i18n.language?.startsWith(l.key)
                    ? 'bg-forest-700 text-white'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <h1 className="mb-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{t('login.title')}</h1>
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">{t('login.subtitle')}</p>
          <form onSubmit={handleSubmit((d) => mut.mutate(d))} noValidate>
            <FormField label={t('login.username')} htmlFor="username" error={errors.username?.message} required>
              <Input id="username" autoComplete="username" {...register('username')} />
            </FormField>
            <FormField label={t('login.password')} htmlFor="password" error={errors.password?.message} required>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}
                >
                  {showPassword ? <EyeOff className="h-4 w-4 text-slate-400 dark:text-slate-500" /> : <Eye className="h-4 w-4 text-slate-400 dark:text-slate-500" />}
                </button>
              </div>
            </FormField>
            <Button type="submit" className="mt-2 w-full" loading={mut.isPending}>
              {t('login.submit')}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-400">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="font-medium text-forest-700 hover:underline dark:text-forest-300">
              {t('login.register')}
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-slate-500 dark:text-slate-400">
            <Link to="/contact" className="hover:underline">
              {t('login.contact', { defaultValue: 'Contact' })}
            </Link>
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
